import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api } from "../api";
import type { CaseView } from "shared";
import { useAuth } from "../auth";
import type { Followup } from "../caseApi";
import { localTz } from "./FollowUps";

type Cat = { id: string; slug: string; name: string; programCount: number; children: { slug: string; name: string; programCount: number }[] };

export function Home() {
  const { me } = useAuth();
  const cats = useQuery({ queryKey: ["categories"], queryFn: () => api<{ categories: Cat[] }>("/api/directory/categories") });
  const today = useQuery({ queryKey: ["followups", "today"], queryFn: () => api<{ followups: Followup[] }>(`/api/followups?due=today&tz=${encodeURIComponent(localTz())}`) });
  const mine = useQuery({ queryKey: ["cases", "mine"], queryFn: () => api<{ cases: CaseView[] }>("/api/cases?scope=mine") });
  return (
    <div className="mx-auto max-w-5xl">
      <h1 className="mb-1 text-xl font-semibold">Welcome, {me?.user.name}</h1>
      <div className="mb-8 grid gap-4 md:grid-cols-2">
        <section className="card" aria-label="My follow-ups today">
          <h2 className="mb-2 text-sm font-semibold">My follow-ups today</h2>
          {today.data?.followups.length === 0 && <p className="text-sm text-slate-500">Nothing due today.</p>}
          <ul className="space-y-1 text-sm">
            {today.data?.followups.map((f) => (
              <li key={f.id}><Link to="/cases/$id" params={{ id: f.caseId }} className="font-medium text-brand-700 hover:underline dark:text-brand-100">{f.caseTitle}</Link>
                {f.note && <span className="text-slate-600 dark:text-slate-400"> · {f.note}</span>}</li>
            ))}
          </ul>
        </section>
        <section className="card" aria-label="My open cases">
          <h2 className="mb-2 text-sm font-semibold">My open cases</h2>
          {mine.data?.cases.length === 0 && <p className="text-sm text-slate-500">No open cases. Press n to start one.</p>}
          <ul className="space-y-1 text-sm">
            {mine.data?.cases.slice(0, 8).map((c) => (
              <li key={c.id}><Link to="/cases/$id" params={{ id: c.id }} className="font-medium text-brand-700 hover:underline dark:text-brand-100">{c.title}</Link>
                <span className="text-slate-600 dark:text-slate-400"> · {c.customer.name}</span></li>
            ))}
          </ul>
        </section>
      </div>
      <h2 className="mb-1 text-sm font-semibold">Assistance categories</h2>
      <p className="mb-4 text-sm text-slate-500">Browse the directory by category.</p>
      {cats.isLoading && <p className="text-sm text-slate-500">Loading…</p>}
      {cats.error && <p role="alert" className="text-sm text-red-600">Could not load categories.</p>}
      {cats.data && cats.data.categories.length === 0 && <p className="text-sm text-slate-500">No content has been imported yet.</p>}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cats.data?.categories.map((c) => (
          <Link key={c.id} to="/directory" search={{ category: c.slug }} className="card hover:border-brand-500" data-category={c.slug}>
            <div className="font-semibold">{c.name}</div>
            <div className="text-xs text-slate-500">{c.programCount} programs</div>
          </Link>
        ))}
      </div>
    </div>
  );
}
